/**
 * ParagraphHighlight Component
 *
 * Color overlay on the original Arabic paragraph in the PDF.
 * Positioned absolutely within the page container.
 *
 * Features:
 * - Semi-transparent background matching paragraph color
 * - mix-blend-mode for natural overlay effect
 * - Hover state with STRONGER but still semi-transparent tint
 * - Non-interactive (pointer-events: none)
 *
 * IMPORTANT: Both normal and hover states use semi-transparent colors
 * so the underlying PDF text remains readable at all times.
 */

import React from "react";
import { PdfParagraph } from "./translationTypes.js";
import { getColorForIndex, getTransparentBg } from "./colorPalette.js";

interface ParagraphHighlightProps {
  /** The paragraph to highlight */
  paragraph: PdfParagraph;
  /** Color index for this paragraph */
  colorIndex: number;
  /** Whether this highlight is currently hovered */
  isHovered: boolean;
  /** Page dimensions for coordinate conversion */
  pageWidth: number;
  pageHeight: number;
  /** Page offset from viewport top-left */
  pageOffsetX: number;
  pageOffsetY: number;
}

export function ParagraphHighlight({
  paragraph,
  colorIndex,
  isHovered,
  pageWidth,
  pageHeight,
  pageOffsetX,
  pageOffsetY,
}: ParagraphHighlightProps) {
  // Opacity settings: both states remain semi-transparent
  // so the underlying PDF text is always readable
  const baseOpacity = 0.25;   // Subtle tint when not hovered
  const hoverOpacity = 0.45;  // Stronger but still readable when hovered

  // Convert normalized bbox (0-1) to pixel coordinates
  const left = pageOffsetX + paragraph.bbox.x0 * pageWidth;
  const top = pageOffsetY + paragraph.bbox.y0 * pageHeight;
  const width = (paragraph.bbox.x1 - paragraph.bbox.x0) * pageWidth;
  const height = (paragraph.bbox.y1 - paragraph.bbox.y0) * pageHeight;

  // Add some padding for visual comfort
  const padding = 4;

  // Always use semi-transparent background
  const backgroundColor = getTransparentBg(colorIndex, isHovered ? hoverOpacity : baseOpacity);

  return (
    <div
      className="paragraphHighlight"
      style={{
        position: "absolute",
        left: left - padding,
        top: top - padding,
        width: width + padding * 2,
        height: height + padding * 2,
        backgroundColor,
        borderRadius: 4,
        pointerEvents: "none",
        transition: "background-color 0.2s ease",
        mixBlendMode: "multiply",
        zIndex: 50,
      }}
    />
  );
}

export default ParagraphHighlight;
